import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface Command {
    id: string;
    label: string;
    category: string;
    defaultShortcut?: string;
    shortcut?: string; // User assigned
    action: () => void;
    description?: string;
    /**
     * Key-handling scope of the owning workbench. 'sketch' commands are
     * resolved by SketchMode's capture-phase handler and are never fired by
     * the global KeyboardListener outside the /sketch route.
     */
    scope?: string;
}

// The legacy first-draft `useCommandStore` was removed: zero references and
// its persist middleware still attached a 'storage' event listener per tab.
// `useCommandManager` below is the single command registry.

// Revised store to handle persistence of shortcuts separately from command definitions
interface UserShortcuts {
    [commandId: string]: string;
}

export interface CommandStore {
    commands: Record<string, Command>;
    userShortcuts: UserShortcuts;

    registerCommand: (command: Omit<Command, 'shortcut'>) => void;
    /** Start buffering registrations (startup batch); `end` flushes them in ONE set(). */
    deferRegistrations: (batch: () => void) => void;
    unregisterCommand: (id: string) => void;
    updateShortcut: (id: string, shortcut: string) => void;
    resetShortcuts: () => void;
    executeCommand: (id: string) => void;
    getCommandByShortcut: (shortcut: string) => Command | undefined;
    /** True when a plain (non-modifier) shortcut exists that extends `prefix`. */
    hasCommandWithPrefix: (prefix: string) => boolean;
}

/**
 * Whether a (lowercased, trimmed) shortcut string is a typable plain
 * sequence like "WA" or "T". Named keys (Esc, Delete, Enter, F1-F12) and
 * modifier combos are excluded: they have their own handling paths and must
 * not participate in the character sequence buffer.
 */
const isTypedSequence = (sc: string) =>
    /^[a-z0-9]+$/.test(sc) && !/^(esc|delete|enter|f\d{1,2})$/.test(sc);

export const useCommandManager = create<CommandStore>()(
    persist(
        (set, get) => {
            /**
             * Registration batching (startup performance): CommandRegistrar
             * registers ~300 commands in one mount effect. Each plain
             * `registerCommand` is a separate set(), so every `commands`
             * subscriber (Toolbar, ContextMenu, Ribbon, shortcut help) used to
             * re-render ~300 times during the burst. While `deferDepth > 0`,
             * pending commands accumulate here and a single flush publishes
             * them as one store update.
             */
            let deferDepth = 0;
            let pending: Array<Omit<Command, 'shortcut'>> = [];

            const mergeIntoCommands = (
                commands: Record<string, Command>,
                userShortcuts: UserShortcuts,
                batch: Array<Omit<Command, 'shortcut'>>
            ): Record<string, Command> => {
                if (batch.length === 0) return commands;
                const next = { ...commands };
                for (const command of batch) {
                    next[command.id] = {
                        ...command,
                        shortcut: userShortcuts[command.id] ?? command.defaultShortcut,
                    };
                }
                return next;
            };

            const registerCommand = (command: Omit<Command, 'shortcut'>) => {
                if (deferDepth > 0) {
                    // Last-write-wins for buffered duplicates: keep the newest
                    // definition for an id that is registered twice in a batch.
                    const at = pending.findIndex(p => p.id === command.id);
                    if (at >= 0) pending.splice(at, 1);
                    pending.push(command);
                    return;
                }
                set((state) => ({
                    commands: mergeIntoCommands(state.commands, state.userShortcuts, [command]),
                }));
            };

            return {
            commands: {},
            userShortcuts: {},

            registerCommand,

            deferRegistrations: (batch) => {
                deferDepth++;
                try {
                    batch();
                } finally {
                    deferDepth--;
                    if (deferDepth === 0) {
                        const toFlush = pending;
                        pending = [];
                        if (toFlush.length > 0) {
                            set((state) => ({
                                commands: mergeIntoCommands(state.commands, state.userShortcuts, toFlush),
                            }));
                        }
                    }
                }
            },

            unregisterCommand: (id) => {
                set((state) => {
                    const newCommands = { ...state.commands };
                    delete newCommands[id];
                    return { commands: newCommands };
                });
            },

            updateShortcut: (id, shortcut) => {
                set((state) => ({
                    userShortcuts: {
                        ...state.userShortcuts,
                        [id]: shortcut
                    },
                    commands: {
                        ...state.commands,
                        [id]: {
                            ...state.commands[id],
                            shortcut: shortcut
                        }
                    }
                }));
            },

            resetShortcuts: () => {
                set((state) => {
                    // Clear user shortcuts
                    const newCommands = { ...state.commands };
                    Object.keys(newCommands).forEach(key => {
                        newCommands[key].shortcut = newCommands[key].defaultShortcut;
                    });
                    return {
                        userShortcuts: {},
                        commands: newCommands
                    };
                });
            },

            executeCommand: (id) => {
                const command = get().commands[id];
                if (command) {
                    console.log(`Executing command: ${command.label} (${id})`);
                    try {
                        command.action();
                    } catch (error) {
                        console.error(`Error executing command ${id}:`, error);
                    }
                } else {
                    // Never drop silently: an unregistered id (typo in a
                    // ribbon/toolbar/registry wiring) must surface in logs.
                    console.warn(`[CommandStore] executeCommand: no command registered for id '${id}'.`);
                }
            },

            getCommandByShortcut: (shortcut) => {
                const key = shortcut.trim().toLowerCase();
                if (!key) return undefined;
                const commands = get().commands;
                return Object.values(commands).find(cmd =>
                    (cmd.shortcut ?? cmd.defaultShortcut ?? '').trim().toLowerCase() === key
                );
            },

            hasCommandWithPrefix: (prefix) => {
                const key = prefix.trim().toLowerCase();
                if (!key) return false;
                const commands = get().commands;
                // Only typable plain sequences extend the buffer; named keys
                // (Esc, F1...) and modifier combos have their own paths.
                return Object.values(commands).some(cmd => {
                    const sc = (cmd.shortcut ?? cmd.defaultShortcut ?? '').trim().toLowerCase();
                    return isTypedSequence(sc) && sc.startsWith(key) && sc !== key;
                });
            }
            };
        },
        {
            name: 'yap-command-shortcuts',
            partialize: (state) => ({ userShortcuts: state.userShortcuts }),
            // Migration: shortcuts saved under pre-rename command ids are
            // remapped so user customizations survive the neutral-id rename.
            merge: (persisted, current) => {
                const p = (persisted ?? {}) as Partial<CommandStore>;
                const REMAP_IDS: Record<string, string> = {
                    activateLinkRevit: 'activateLinkBim',
                    activateLoadAutodeskFamily: 'activateLoadCloudFamily',
                    'sys.grasshopper': 'sys.visualScripting',
                };
                const userShortcuts = Object.fromEntries(
                    Object.entries(p.userShortcuts ?? {}).map(([id, keys]) => [REMAP_IDS[id] ?? id, keys])
                );
                return { ...current, ...p, userShortcuts };
            },
        }
    )
);
